import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Upload,
  Button,
  Divider,
  notification,
  Select,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { addProduct, updateProduct } from "./adminApi";
import { BoxAdd, Edit, Back, Send } from "iconsax-react";

const ProductModal = ({ visible, onClose, product, refreshData }) => {
  const [form] = Form.useForm();
  const [thumbnailFileList, setThumbnailFileList] = useState([]);
  const [imagesFileList, setImagesFileList] = useState([]);
  const [loading, setLoading] = useState(false);

  const urlToFile = async (url, filename) => {
    const res = await fetch(url);
    const blob = await res.blob();
    return new File([blob], filename, { type: blob.type });
  };

  useEffect(() => {
    if (product?.data) {
      const data = product.data;
      form.setFieldsValue(data);

      setThumbnailFileList(
        data.thumbnail
          ? [
              {
                uid: "-1",
                name: "thumbnail.jpg",
                status: "done",
                url: data.thumbnail,
              },
            ]
          : []
      );

      setImagesFileList(
        data.images?.map((img) => ({
          uid: img.id.toString(),
          name: "image.jpg",
          status: "done",
          url: img.path,
        })) || []
      );
    } else {
      form.resetFields();
      setThumbnailFileList([]);
      setImagesFileList([]);
    }
  }, [product, form]);

  const handleClose = () => {
    form.resetFields();
    setThumbnailFileList([]);
    setImagesFileList([]);
    onClose();
  };

  const handleSubmit = async () => {
    try {
      setLoading(true); // ✅ Mulai loading

      const values = await form.validateFields();
      const formData = new FormData();

      values.price = 0;
      values.stock = 0;

      for (let key in values) {
        if (key === "thumbnail" || key === "images") continue;
        formData.append(key, values[key]);
      }

      if (thumbnailFileList.length > 0) {
        const thumb = thumbnailFileList[0];
        if (thumb.originFileObj) {
          formData.append("thumbnail", thumb.originFileObj);
        } else if (thumb.url) {
          const file = await urlToFile(thumb.url, "thumbnail.jpg");
          formData.append("thumbnail", file);
        }
      }

      for (const img of imagesFileList) {
        if (img.originFileObj) {
          formData.append("images[]", img.originFileObj);
        } else if (img.url) {
          const file = await urlToFile(img.url, "image.jpg");
          formData.append("images[]", file);
        }
      }

      if (product) {
        await updateProduct(product.data.id, formData);
        window.alert("✅ Product updated successfully");
      } else {
        await addProduct(formData);
        window.alert("✅ Product added successfully");
        notification.success({
          message: "Success",
          description: "Product added successfully",
          placement: "topRight",
        });
      }

      handleClose();
      refreshData();
    } catch (error) {
      console.error("Submit error:", error);
      const errMsg =
        error.response?.data?.message ||
        error.message ||
        "❌ Failed to submit form. Please try again.";
      window.alert(errMsg);
    } finally {
      setLoading(false); // ✅ Berhenti loading
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={[
        <Button
          key="cancel"
          onClick={handleClose}
          icon={<Back size={18} color="#0000ff" />}
          style={{ borderRadius: "0.5rem" }}
        >
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          icon={<Send size={18} color="#fff" />}
          loading={loading} // ✅ Tampilkan spinner saat loading
          style={{ borderRadius: "0.5rem" }}
        >
          {product ? "Update" : "Create"}
        </Button>,
      ]}
      title={
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div className="modal-header-title">
            {product ? (
              <Edit size={24} color="#1677ff" />
            ) : (
              <BoxAdd size={24} color="#1677ff" />
            )}
            {product ? "Edit Product" : "Add Product"}
          </div>
          <Divider style={{ margin: "8px 0 0 0" }} />
        </div>
      }
      destroyOnClose
      bodyStyle={{ maxHeight: "60vh", overflowY: "auto", paddingBottom: 0 }}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="title" label="Title" rules={[{ required: true }]}>
          <Input />
        </Form.Item>

        {/* 
        <Form.Item name="price" label="Price" rules={[{ required: true }]}>
          <InputNumber
            min={1}
            className="w-full"
            formatter={(value) =>
              !value ? "" : `Rp ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
            }
            parser={(value) => value.replace(/[^0-9]/g, "")}
            onKeyPress={(event) => {
              if (event.charCode < 48 || event.charCode > 57) {
                event.preventDefault();
              }
            }}
          />
        </Form.Item>

        <Form.Item name="stock" label="Stock" rules={[{ required: true }]}>
          <InputNumber
            min={0}
            className="w-full"
            onKeyPress={(event) => {
              if (event.charCode < 48 || event.charCode > 57) {
                event.preventDefault();
              }
            }}
          />
        </Form.Item> */}

        <Form.Item name="brand" label="Brand" rules={[{ required: true }]}>
          <Input />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
          rules={[{ required: true }]}
        >
          <Input.TextArea rows={3} />
        </Form.Item>

        <Form.Item
          name="thumbnail"
          label="Thumbnail"
          rules={[{ required: true }]}
        >
          <Upload
            maxCount={1}
            beforeUpload={(file) => {
              const isImage = ["image/jpeg", "image/png", "image/jpg"].includes(
                file.type
              );
              if (!isImage) {
                window.alert("Only JPG, JPEG, or PNG files are allowed.");
              }
              return isImage ? false : Upload.LIST_IGNORE;
            }}
            fileList={thumbnailFileList}
            onChange={({ fileList }) => {
              if (fileList.length <= 1) {
                setThumbnailFileList(fileList);
              }
            }}
            listType="picture"
          >
            <Button
              icon={<UploadOutlined />}
              disabled={thumbnailFileList.length >= 1}
            >
              Select Thumbnail
            </Button>
          </Upload>
        </Form.Item>

        <Form.Item
          name="images"
          label="Images (Max 5 images)"
          rules={[{ required: true }]}
        >
          <Upload
            multiple
            maxCount={5}
            beforeUpload={(file) => {
              const isImage = ["image/jpeg", "image/png", "image/jpg"].includes(
                file.type
              );
              if (!isImage) {
                window.alert("Only JPG, JPEG, or PNG files are allowed.");
              }
              return isImage ? false : Upload.LIST_IGNORE;
            }}
            fileList={imagesFileList}
            onChange={({ fileList }) => {
              if (fileList.length <= 5) {
                setImagesFileList(fileList);
              }
            }}
            listType="picture"
          >
            <Button
              icon={<UploadOutlined />}
              disabled={imagesFileList.length >= 5}
            >
              Select Images
            </Button>
          </Upload>
        </Form.Item>
        <Form.Item
          name="category_code"
          label="Category"
          rules={[{ required: true, message: "Please select a category" }]}
        >
          <Select placeholder="Select a category">
            <Select.Option value="CO1">Chemical</Select.Option>
            <Select.Option value="C02">Laboratory Equipment</Select.Option>
            <Select.Option value="C03">Mining Equipment</Select.Option>
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ProductModal;
